import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// Interfaces
interface Prediction {
  id: number;
  influencer_id?: number;
  expert_name: string;
  price_prediction: string;
  prediction_type: string;
  success_status: string;
  start_date: string;
  end_date: string;
  source_url?: string;
  price_min?: number;
  price_max?: number;
  created_at: string;
  prediction_made?: string;
  updated_at?: string;
  quote?: string;
}

// Styled Components
const ReviewContainer = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: #ffffff;
  margin-bottom: 20px;
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: #0f0f0f;
  padding: 15px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #333;
`;

const StatNumber = styled.div`
  font-size: 1.8em;
  font-weight: bold;
  color: #4299e1;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #888;
  font-size: 0.9em;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 1px solid #333;
  border-radius: 4px;
  background: ${props => props.active ? '#4299e1' : '#0f0f0f'};
  color: ${props => props.active ? '#ffffff' : '#888'};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#3182ce' : '#1a1a1a'};
    border-color: #4299e1;
  }
`;

const PredictionCard = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  transition: all 0.2s;
  
  &:hover {
    border-color: #4299e1;
  }
`;

const PredictionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const ExpertInfo = styled.div`
  flex: 1;
`;

const ExpertName = styled.h3`
  color: #ffffff;
  margin: 0 0 5px 0;
  font-size: 16px;
`;

const PredictionDetails = styled.div`
  color: #888;
  font-size: 14px;
  margin: 5px 0;
`;

const QuoteSection = styled.div`
  background: #0a0a0a;
  border-left: 3px solid #4299e1;
  border-radius: 4px;
  padding: 12px 16px;
  margin: 10px 0;
  font-style: italic;
  color: #ccc;
  font-size: 14px;
  line-height: 1.4;
  
  &:before {
    content: '"';
    color: #4299e1;
    font-size: 18px;
  }
  
  &:after {
    content: '"';
    color: #4299e1;
    font-size: 18px;
  }
`;

const LastUpdatedInfo = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 8px 12px;
  margin: 10px 0;
  font-size: 12px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .icon {
    color: #4299e1;
  }
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 15px 0;
`;

const StatusLabel = styled.span`
  color: #ffffff;
  font-weight: bold;
  min-width: 80px;
`;

const StatusSelect = styled.select`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 8px 12px;
  color: #ffffff;
  font-size: 14px;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'success':
        return `
          background: #22c55e;
          color: white;
        `;
      case 'miss':
        return `
          background: #ef4444;
          color: white;
        `;
      case 'pending':
        return `
          background: #f59e0b;
          color: white;
        `;
      case 'delayed':
        return `
          background: #8b5cf6;
          color: white;
        `;
      case 'updated':
        return `
          background: #06b6d4;
          color: white;
        `;
      default:
        return `
          background: #6b7280;
          color: white;
        `;
    }
  }}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: string; disabled?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  ${props => {
    switch (props.variant) {
      case 'save':
        return `
          background: #22c55e;
          color: white;
          &:hover:not(:disabled) { background: #16a34a; }
        `;
      case 'reset':
        return `
          background: #6b7280;
          color: white;
          &:hover:not(:disabled) { background: #4b5563; }
        `;
      case 'view':
        return `
          background: #3b82f6;
          color: white;
          &:hover:not(:disabled) { background: #2563eb; }
        `;
      case 'tweet':
        return `
          background: #1d4ed8;
          color: white;
          &:hover:not(:disabled) { background: #1e40af; }
        `;
      case 'refresh':
        return `
          background: #f59e0b;
          color: white;
          &:hover:not(:disabled) { background: #d97706; }
        `;
      default:
        return `
          background: #333;
          color: #fff;
          border: 1px solid #555;
          &:hover:not(:disabled) { background: #444; }
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #888;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
`;

const CurrentPriceDisplay = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 10px 15px;
  margin-bottom: 20px;
  text-align: center;
  
  .price {
    color: #22c55e;
    font-size: 1.2em;
    font-weight: bold;
    margin: 0 5px;
  }
  
  .label {
    color: #888;
    font-size: 0.9em;
  }
`;

interface PredictionReviewProps {
  theme: any;
}

const PredictionReview: React.FC<PredictionReviewProps> = ({ theme }) => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [filter, setFilter] = useState<string>('all');
  const [editingStatus, setEditingStatus] = useState<{ [key: number]: string }>({});
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [sendingTweetIds, setSendingTweetIds] = useState<Set<number>>(new Set());

  // Load all predictions
  const loadPredictions = async () => {
    try {
      setLoading(true);
      
      // Get current Bitcoin price
      const priceResponse = await axios.get(`${apiBase}/api/admin/current-price`);
      setCurrentPrice(priceResponse.data.price || 0);
      
      // Get all predictions
      const response = await axios.get(`${apiBase}/api/admin/predictions-all`);
      
      if (response.data.success) {
        setPredictions(response.data.predictions);
        console.log(`📊 Loaded ${response.data.predictions.length} predictions`);
      } else {
        console.error('Failed to load predictions:', response.data.message);
        setPredictions([]);
      }
    } catch (error) {
      console.error('Error loading predictions:', error);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPredictions();
  }, []);

  // Save status change
  const saveStatusChange = async (predictionId: number, newStatus: string) => {
    try {
      setSavingIds(prev => new Set(prev).add(predictionId));
      
      const response = await axios.put(`${apiBase}/api/admin/predictions/${predictionId}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        // Update local state
        setPredictions(prev => prev.map(p => 
          p.id === predictionId ? { ...p, success_status: newStatus } : p
        ));
        
        // Clear editing state
        setEditingStatus(prev => {
          const newState = { ...prev };
          delete newState[predictionId];
          return newState;
        });
        
        console.log(`✅ Updated prediction ${predictionId} status to ${newStatus}`);
      } else {
        alert('Failed to update status: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error saving status:', error);
      alert('Failed to update status: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(predictionId);
        return newSet;
      });
    }
  };

  // Reset status to original
  const resetStatus = (predictionId: number) => {
    setEditingStatus(prev => {
      const newState = { ...prev };
      delete newState[predictionId];
      return newState;
    });
  };

  // Send prediction to tweet pipeline
  const sendToTweetPipeline = async (prediction: Prediction) => {
    try {
      setSendingTweetIds(prev => new Set(prev).add(prediction.id));
      
      const formData = new FormData();
      
      // Determine the post type based on status
      let postType = 'new_prediction';
      if (prediction.success_status === 'updated') {
        postType = 'updated_prediction';
      } else if (prediction.success_status === 'miss') {
        postType = 'prediction_missed';
      } else if (prediction.success_status === 'delayed') {
        postType = 'prediction_delayed';
      } else if (prediction.success_status === 'success') {
        postType = 'prediction_success';
      }

      // Use AI-enhanced tweet generation
      const response = await axios.post(`${apiBase}/api/admin/generate-ai-tweet`, {
        prediction_id: prediction.id,
        post_type: postType,
        options: {
          include_person_context: true,
          no_hashtags: true,
          style: 'engaging_professional'
        }
      });

      let tweetContent = '';
      if (response.data.success && response.data.tweet) {
        tweetContent = response.data.tweet;
        console.log('✅ Using AI-generated tweet content');
      } else {
        // Fallback to enhanced manual template
        const expertName = prediction.expert_name || 'Unknown Expert';
        const predictionText = prediction.price_prediction || '';
        const endDate = prediction.end_date ? formatDate(prediction.end_date) : 'TBD';
        
        // Create different content based on post type
        if (postType === 'new_prediction') {
          tweetContent = `🆕 NEW PREDICTION ALERT
          
${expertName} just made a Bitcoin prediction: ${predictionText}
Target date: ${endDate}

Current BTC: $${currentPrice.toLocaleString()}

Will they be right? Time will tell! ⏰`;
        } else if (postType === 'updated_prediction') {
          tweetContent = `📝 PREDICTION UPDATED
          
${expertName} has updated their Bitcoin prediction
New target: ${predictionText}

Revised timeline: ${endDate}
Current BTC: $${currentPrice.toLocaleString()}

Strategy adjustments in volatile markets 📊`;
        } else if (postType === 'prediction_missed') {
          tweetContent = `❌ PREDICTION MISSED TARGET
          
${expertName}'s Bitcoin prediction: ${predictionText}
Target was: ${endDate}

Current BTC: $${currentPrice.toLocaleString()}

Not all predictions hit - that's crypto! 🎢`;
        } else if (postType === 'prediction_delayed') {
          tweetContent = `⏰ PREDICTION: RIGHT PRICE, WRONG TIMING
          
${expertName} called Bitcoin ${predictionText} 
Target was ${endDate}, but it took longer!

Sometimes accuracy beats speed 🎯`;
        } else {
          // Default active prediction
          tweetContent = `📈 ACTIVE PREDICTION
          
${expertName} predicts Bitcoin: ${predictionText}
Target: ${endDate}

Current price: $${currentPrice.toLocaleString()}
Will they be right? 🤔`;
        }
      }

      const title = `${postType.replace('_', ' ').toUpperCase()}: ${prediction.expert_name} - ${prediction.price_prediction}`;
      
      formData.append('title', title);
      formData.append('content', tweetContent);
      formData.append('topic', 'prediction');
      formData.append('prediction_id', prediction.id.toString());
      formData.append('influencer_id', (prediction.influencer_id || 0).toString());
      formData.append('created_by', 'admin_prediction_review');
      formData.append('post_type', postType);
      
      const pipelineResponse = await axios.post(`${apiBase}/api/tweet-pipeline`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (pipelineResponse.data.success) {
        console.log(`✅ Prediction ${prediction.id} sent to tweet pipeline successfully`);
        alert(`✅ ${postType.replace('_', ' ').toUpperCase()} post sent to Tweet Pipeline!

Title: ${title}

Content preview:
${tweetContent.substring(0, 200)}...`);
      } else {
        console.error('❌ Failed to send to tweet pipeline:', pipelineResponse.data.error);
        alert('❌ Failed to send to tweet pipeline: ' + pipelineResponse.data.error);
      }
    } catch (error: any) {
      console.error('Error sending to tweet pipeline:', error);
      alert('❌ Failed to send to tweet pipeline: ' + (error.response?.data?.message || error.message));
    } finally {
      setSendingTweetIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(prediction.id);
        return newSet;
      });
    }
  };

  // Filter predictions
  const filteredPredictions = predictions.filter(prediction => {
    if (filter === 'all') return true;
    return prediction.success_status === filter;
  });

  // Stats
  const stats = {
    total: predictions.length,
    pending: predictions.filter(p => p.success_status === 'pending').length,
    success: predictions.filter(p => p.success_status === 'success').length,
    delayed: predictions.filter(p => p.success_status === 'delayed').length,
    miss: predictions.filter(p => p.success_status === 'miss').length,
    updated: predictions.filter(p => p.success_status === 'updated').length
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatPrice = (price: any): string => {
    if (!price) return 'N/A';
    const num = typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : price;
    return isNaN(num) ? price.toString() : `$${num.toLocaleString()}`;
  };

  if (loading) {
    return (
      <ReviewContainer>
        <LoadingSpinner>Loading predictions...</LoadingSpinner>
      </ReviewContainer>
    );
  }

  return (
    <ReviewContainer>
      <SectionTitle>
        🔍 Prediction Review & Status Editor
      </SectionTitle>

      {/* Current Price Display */}
      <CurrentPriceDisplay>
        <span className="label">Current Bitcoin Price:</span>
        <span className="price">${currentPrice.toLocaleString()}</span>
        <span className="label">• Quick Status Editor</span>
      </CurrentPriceDisplay>

      {/* Stats */}
      <StatsRow>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total Predictions</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.pending}</StatNumber>
          <StatLabel>Pending Review</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.success}</StatNumber>
          <StatLabel>Successful</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.delayed}</StatNumber>
          <StatLabel>Delayed</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.miss}</StatNumber>
          <StatLabel>Missed</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.updated}</StatNumber>
          <StatLabel>Updated</StatLabel>
        </StatCard>
      </StatsRow>

      {/* Filter Buttons */}
      <FilterSection>
        <FilterButton 
          active={filter === 'all'} 
          onClick={() => setFilter('all')}
        >
          All ({stats.total})
        </FilterButton>
        <FilterButton 
          active={filter === 'pending'} 
          onClick={() => setFilter('pending')}
        >
          Pending ({stats.pending})
        </FilterButton>
        <FilterButton 
          active={filter === 'success'} 
          onClick={() => setFilter('success')}
        >
          Success ({stats.success})
        </FilterButton>
        <FilterButton 
          active={filter === 'delayed'} 
          onClick={() => setFilter('delayed')}
        >
          Delayed ({stats.delayed})
        </FilterButton>
        <FilterButton 
          active={filter === 'miss'} 
          onClick={() => setFilter('miss')}
        >
          Miss ({stats.miss})
        </FilterButton>
        <FilterButton 
          active={filter === 'updated'} 
          onClick={() => setFilter('updated')}
        >
          Updated ({stats.updated})
        </FilterButton>
        <Button 
          variant="refresh"
          onClick={loadPredictions}
        >
          🔄 Refresh
        </Button>
      </FilterSection>

      {/* Predictions List */}
      {filteredPredictions.length === 0 ? (
        <EmptyState>
          No predictions found for the selected filter.
        </EmptyState>
      ) : (
        filteredPredictions.map((prediction) => {
          const isEditing = editingStatus.hasOwnProperty(prediction.id);
          const isSaving = savingIds.has(prediction.id);
          const currentEditStatus = editingStatus[prediction.id] || prediction.success_status;

          return (
            <PredictionCard key={prediction.id}>
              <PredictionHeader>
                <ExpertInfo>
                  <ExpertName>
                    {prediction.expert_name || 'Unknown Expert'}
                  </ExpertName>
                  <PredictionDetails>
                    <strong>{prediction.prediction_type?.toUpperCase() || 'UNKNOWN'}:</strong> {prediction.price_prediction}
                  </PredictionDetails>
                  <PredictionDetails>
                    Period: {formatDate(prediction.start_date)} → {formatDate(prediction.end_date)}
                  </PredictionDetails>
                  <PredictionDetails>
                    Created: {formatDate(prediction.created_at)}
                    {prediction.prediction_made && ` • Made: ${formatDate(prediction.prediction_made)}`}
                  </PredictionDetails>
                  {prediction.source_url && (
                    <PredictionDetails>
                      <a 
                        href={prediction.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#4299e1', textDecoration: 'none' }}
                      >
                        🔗 Source URL
                      </a>
                    </PredictionDetails>
                  )}
                  
                  {prediction.quote && (
                    <QuoteSection>
                      {prediction.quote}
                    </QuoteSection>
                  )}
                  
                  {prediction.updated_at && (
                    <LastUpdatedInfo>
                      <span className="icon">📝</span>
                      Last reviewed: {formatDate(prediction.updated_at)}
                    </LastUpdatedInfo>
                  )}
                </ExpertInfo>
                
                <div>
                  <PredictionDetails style={{ textAlign: 'right', marginBottom: '10px' }}>
                    ID: #{prediction.id}
                  </PredictionDetails>
                  {!isEditing && (
                    <StatusBadge status={prediction.success_status}>
                      {prediction.success_status}
                    </StatusBadge>
                  )}
                </div>
              </PredictionHeader>

              {/* Status Editor */}
              <StatusSection>
                <StatusLabel>Status:</StatusLabel>
                {isEditing ? (
                  <StatusSelect
                    value={currentEditStatus}
                    onChange={(e) => setEditingStatus(prev => ({
                      ...prev,
                      [prediction.id]: e.target.value
                    }))}
                    disabled={isSaving}
                  >
                    <option value="pending">Pending - Still active</option>
                    <option value="success">Success - Prediction correct</option>
                    <option value="delayed">Delayed - Price right, timing wrong</option>
                    <option value="miss">Miss - Prediction failed</option>
                    <option value="updated">Updated - Prediction modified</option>
                  </StatusSelect>
                ) : (
                  <StatusBadge status={prediction.success_status}>
                    {prediction.success_status}
                  </StatusBadge>
                )}
              </StatusSection>

              {/* Action Buttons */}
              <ButtonGroup>
                {isEditing ? (
                  <>
                    <Button
                      variant="save"
                      onClick={() => saveStatusChange(prediction.id, currentEditStatus)}
                      disabled={isSaving || currentEditStatus === prediction.success_status}
                    >
                      {isSaving ? '⏳ Saving...' : '💾 Save Status'}
                    </Button>
                    <Button
                      variant="reset"
                      onClick={() => resetStatus(prediction.id)}
                      disabled={isSaving}
                    >
                      ❌ Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => setEditingStatus(prev => ({
                      ...prev,
                      [prediction.id]: prediction.success_status
                    }))}
                    disabled={isSaving}
                  >
                    ✏️ Edit Status
                  </Button>
                )}
                
                {prediction.source_url && (
                  <Button
                    variant="view"
                    onClick={() => window.open(prediction.source_url, '_blank')}
                  >
                    🔗 View Source
                  </Button>
                )}
                
                <Button
                  variant="tweet"
                  onClick={() => sendToTweetPipeline(prediction)}
                  disabled={savingIds.has(prediction.id) || sendingTweetIds.has(prediction.id)}
                >
                  {sendingTweetIds.has(prediction.id) ? '⏳ Sending...' : '🚀 Send to Tweet Pipeline'}
                </Button>
              </ButtonGroup>
            </PredictionCard>
          );
        })
      )}
    </ReviewContainer>
  );
};

export default PredictionReview;
